import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient, useAuthStore } from '@indipia/shared'
import type { TaskSignal } from '@indipia/shared'
import type { SiteInfo } from '../views/CalendarMultiView'
import type { GanttMilestone, ScheduleDetailResponse } from '../types/gantt.types'
import type { ScheduleListRow } from '../types/scheduleList.types'
import { isoDay } from '../constants/signalView'
import { holidayQueryRange } from '../views/scheduleUtils'
import { buildScheduleListRows } from '../constants/scheduleList.logic'
import { useSignalLayer } from './useSignalLayer'
import { useScheduleOnlyCodes } from './useScheduleOnlyCodes'
import { useMaterialOrderData } from './useMaterialOrders'
import { useHolidayRange } from './useHolidays'

// 리스트 보기 데이터 로딩 (구현계획 C-01) — 공종·선행·비고 / 마일스톤 / 신호 / 발주 원장을 읽어 행 모델을 만든다.
//  ① 공종·선행·비고·기간 = GET /schedule/schedules/{id} (간트와 같은 응답, calendar 응답은 쓰지 않음)
//  ② 마일스톤 = GET /schedule/schedules/{id}/milestones
//  ③ 신호 = useSignalLayer(useTaskSignals 재사용, 오늘 할일 건수 포함)
//  ④ 발주 원장 = useMaterialOrderData (팝업·상세창과 같은 쿼리 키 공유)
// 진행 요약(progress-summary)은 공종당 1회라 펼친 행이 useTaskProgressSummary 로 직접 조회한다(D-07).
// 실패는 폴백으로 덮지 않는다: 공종·신호 실패 = 표 대신 명시 오류 + 다시 시도, 발주 실패 = 발주 칸만 부분 오류,
// 마일스톤 실패 = 마일스톤 행만 오류 표시(공종 행은 그대로).
// 신호는 항상 최신(staleTime 0·창 포커스 재조회)이라 신호가 바뀌면 행 순서(급한순)도 바뀐다 — 그대로 반영한다(계획서 6-17).

/** 리스트 쿼리 키 접두. 공종·마일스톤 조회가 쓰며 변경 지점이 이 접두로 무효화한다 */
export const SCHEDULE_LIST_KEY = 'schedule-list'

/** 공종 상세 쿼리 키 — 비고 저장(C-04)이 캐시를 직접 갱신할 때도 같은 키를 쓴다 */
export function scheduleListDetailKey(compId: number | null | undefined, scheduleId: number | null | undefined) {
  return [SCHEDULE_LIST_KEY, 'detail', compId ?? null, scheduleId ?? null] as const
}

export function scheduleListMilestonesKey(compId: number | null | undefined, scheduleId: number | null | undefined) {
  return [SCHEDULE_LIST_KEY, 'milestones', compId ?? null, scheduleId ?? null] as const
}

/** 스케줄 생성·리셋 직후 첫 조회 지연 대비 자동 재시도 — 간트 조회와 같은 간격(ms) */
const RETRY_DELAYS_MS = [800, 1600, 3200]

type PartStatus = 'loading' | 'ready' | 'error'

/** 표 전체를 대신하는 오류 — 공종 조회 실패 또는 신호 조회 실패(SIGNAL_MISSING 503 포함) */
export type ScheduleListBlockError = { kind: 'detail' | 'signal'; retry: () => void }

const EMPTY_ROWS: ScheduleListRow[] = []
const EMPTY_MILESTONES: GanttMilestone[] = []

export function useScheduleListData(site: SiteInfo | undefined) {
  const user = useAuthStore(s => s.user)
  const compId = user?.compId
  const scheduleId = site?.scheduleId ?? null
  // siteId 가 없거나 0 이면 신호를 조회할 수 없다 — 시스템값으로 대체하지 않고 신호 오류로 보인다
  const siteId = site && site.siteId > 0 ? site.siteId : null

  const headers = useMemo(() => ({
    ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }), [user?.compId, user?.memberId])

  // ── ① 공종·선행·비고·기간 ────────────────────────────────────────────
  const detailQ = useQuery<ScheduleDetailResponse>({
    queryKey: scheduleListDetailKey(compId, scheduleId),
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<{ data: ScheduleDetailResponse }>(
        `/schedule/schedules/${scheduleId}`, { headers, signal })
      const data = res.data?.data
      if (!data) throw new Error('스케줄 응답이 비어 있습니다.')
      return data
    },
    enabled: scheduleId != null,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: RETRY_DELAYS_MS.length,
    retryDelay: attempt => RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)],
  })

  // ── ② 마일스톤 ───────────────────────────────────────────────────────
  const milestonesQ = useQuery<GanttMilestone[]>({
    queryKey: scheduleListMilestonesKey(compId, scheduleId),
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<{ data: { milestones: GanttMilestone[] } }>(
        `/schedule/schedules/${scheduleId}/milestones`, { headers, signal })
      const list = res.data?.data?.milestones
      if (!Array.isArray(list)) throw new Error('마일스톤 응답이 비어 있습니다.')
      return list
    },
    enabled: scheduleId != null,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: RETRY_DELAYS_MS.length,
    retryDelay: attempt => RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)],
  })

  // ── ③ 신호(+ 오늘 할일 건수) ─────────────────────────────────────────
  // 스케줄 전용 행정 공정(세대 동의서 청구 등)은 신호등을 그리지 않는다 — [빨강·노랑만]·할 일 개수에서도 뺀다(간트와 같은 기준)
  const scheduleOnlyCodes = useScheduleOnlyCodes()
  const signal = useSignalLayer(siteId, undefined, scheduleOnlyCodes)
  const signalsQ = signal.signalsQ

  // ── ④ 발주 원장 ──────────────────────────────────────────────────────
  const orders = useMaterialOrderData(scheduleId)

  // 휴일(기간 칸 '진행중 경과/n일' 작업일 계산용) — 공종 상세가 오면 스케줄 기간 기준으로 조회. 조회 중·실패면 경과를 만들지 않으며 표는 기다리지 않는다
  const holidayRange = useMemo(
    () => (detailQ.data?.startDate && detailQ.data?.endDate
      ? holidayQueryRange(new Date(detailQ.data.startDate), new Date(detailQ.data.endDate))
      : null),
    [detailQ.data?.startDate, detailQ.data?.endDate],
  )
  const holidayState = useHolidayRange(holidayRange?.from, holidayRange?.to)
  const holidays = holidayState.status === 'success' ? holidayState.dates : null

  // 오늘(로컬 달력일) — 창이 다시 보일 때 날짜가 바뀌었으면 갱신
  const [today, setToday] = useState(() => isoDay(new Date()))
  useEffect(() => {
    const refresh = () => setToday(isoDay(new Date()))
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [])

  // ── 상태 ─────────────────────────────────────────────────────────────
  const detailStatus: PartStatus = detailQ.isError ? 'error' : detailQ.data ? 'ready' : 'loading'
  const signalStatus: PartStatus = scheduleId != null && siteId == null
    ? 'error'
    : signalsQ.isError ? 'error' : signalsQ.data ? 'ready' : 'loading'
  const ordersStatus: PartStatus = orders.status
  const milestonesStatus: PartStatus = milestonesQ.isError ? 'error' : milestonesQ.data ? 'ready' : 'loading'

  const refetchDetail = detailQ.refetch
  const refetchSignals = signalsQ.refetch
  const refetchMilestones = milestonesQ.refetch
  const reloadOrders = orders.reload
  const retryDetail = useCallback(() => { void refetchDetail() }, [refetchDetail])
  const retrySignals = useCallback(() => { void refetchSignals() }, [refetchSignals])
  const retryMilestones = useCallback(() => { void refetchMilestones() }, [refetchMilestones])

  // 표 전체를 대신하는 오류 — 공종 조회 실패가 신호 실패보다 앞선다
  const blockError: ScheduleListBlockError | null =
    detailStatus === 'error' ? { kind: 'detail', retry: retryDetail }
      : signalStatus === 'error' ? { kind: 'signal', retry: retrySignals }
        : null

  // 표를 그릴 수 있는 시점: 공종·신호가 도착하고 발주 조회가 끝남(성공 또는 실패).
  // 발주 조회 중에 그리면 원장이 비어 보여 '거래처 미배정'으로 잘못 나오므로 끝날 때까지 기다린다.
  const ready = blockError == null && detailStatus === 'ready' && signalStatus === 'ready' && ordersStatus !== 'loading'
  const status: 'idle' | 'loading' | 'error' | 'ready' =
    scheduleId == null ? 'idle' : blockError ? 'error' : ready ? 'ready' : 'loading'

  const detail = detailQ.data
  const signals: TaskSignal[] | undefined = signalsQ.data

  // ── 행 모델 ──────────────────────────────────────────────────────────
  const rows = useMemo<ScheduleListRow[]>(() => {
    if (!ready || !detail || !signals) return EMPTY_ROWS
    const built = buildScheduleListRows({
      tasks: detail.tasks,
      deps: detail.dependencies,
      signals,
      vendorRows: ordersStatus === 'ready' ? orders.rows : [],
      today,
      holidays,
      scheduleOnlyCodes,
    })
    // 발주 조회 실패: 발주 칸만 부분 오류로 바꾼다('거래처 미배정'으로 숨기지 않음)
    if (ordersStatus === 'error') {
      return built.map(r => ({ ...r, cells: { ...r.cells, order: { status: 'order-error' as const } } }))
    }
    return built
  }, [ready, detail, signals, ordersStatus, orders.rows, today, holidays, scheduleOnlyCodes])

  const taskNameById = useMemo(() => {
    const m = new Map<number, string>()
    for (const t of detail?.tasks ?? []) m.set(t.taskId, t.workTypeName)
    return m
  }, [detail])

  return {
    /** idle=현장 없음 · loading · error(표 대신 오류) · ready */
    status,
    /** 표 전체를 대신하는 오류(없으면 null) — [다시 시도]는 blockError.retry */
    blockError,
    /** 발주 칸 부분 오류 상태·재시도 */
    ordersStatus,
    retryOrders: reloadOrders,
    /** 마일스톤 조회 상태·재시도 — 오류면 마일스톤 행만 오류 표시 */
    milestonesStatus,
    retryMilestones,
    /** 행 모델(필터·정렬 전 원본 순서) */
    rows,
    /** 마일스톤 원본(조회 전·오류면 빈 배열) */
    milestones: milestonesQ.data ?? EMPTY_MILESTONES,
    /** 공종 상세 응답 원본 — 현장명·기간·비고·공종·선행 */
    detail,
    taskNameById,
    /** 신호 층(오늘 할일 건수·SignalTodoPanel 상태 포함)과 신호 원본 */
    signal,
    signals,
    todoCount: signal.todoCount,
    /** 오늘(로컬 달력일 'YYYY-MM-DD') — 로직 함수에 넘기는 기준일 */
    today,
    /** 신호 대기 중인 재조회(창 포커스 등) 표시용 */
    refreshing: detailQ.isFetching || signalsQ.isFetching || milestonesQ.isFetching,
  }
}

export type ScheduleListData = ReturnType<typeof useScheduleListData>
